import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, GraduationCap, LineChart, Boxes, BookOpen, Compass, Layers,
  TrendingUp, TrendingDown, Calendar, Building2, Users, Briefcase, Calculator, ArrowUpRight,
  Shield, Activity, MessageSquarePlus, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionLabel, DataStamp } from "@/components/Shared";
import { CourseCard, InsightCard, ArticleCard } from "@/components/Cards";
import { Newsletter } from "@/components/Newsletter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  PILLARS, THREE_PILLAR_MODEL, LEARNING_PATH, MAEE,
  RESEARCH_DESK, DASHBOARD_WIDGETS, EVENTS,
} from "@/data/content";

const iconMap = { GraduationCap, LineChart, Boxes };

function Hero() {
  return (
    <section className="relative bg-navy grain overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-20">
        <img src="https://images.unsplash.com/photo-1644088379091-d574269d422f" alt="" className="h-full w-full object-cover animate-drift" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/60" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6 animate-fade-up">
            <span className="h-px w-8 bg-gold" />
            <span className="eyebrow text-gold">Systematic Financial-Market Education</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Understand Markets. Build Systems. Think Independently.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl animate-fade-up" style={{ animationDelay: "0.2s" }}>
            KRV Prime is an independent financial-market education and information platform focused on helping learners
            understand market structure, technical analysis, derivatives, risk management and systematic trading frameworks.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/courses" data-testid="hero-explore-learning" className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors">
              Explore Learning <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/research" data-testid="hero-explore-research" className="inline-flex items-center gap-2 h-12 px-7 rounded-md border border-white/25 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
              Explore Market Insights
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-14 relative z-10" data-testid="pillars-section">
      <div className="grid sm:grid-cols-3 gap-px bg-border rounded-lg overflow-hidden border border-border shadow-lg">
        {PILLARS.map((p, i) => (
          <div key={p.key} className="bg-white p-8 hover:bg-subtle transition-colors">
            <span className="font-display text-4xl font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-display text-xl font-semibold text-navy mt-3">{p.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="about-section">
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionLabel>About KRV Prime</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">Built Around Systematic Market Thinking</h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            KRV Prime was created around a simple idea: financial markets should be studied through structure,
            process and disciplined reasoning rather than noise, speculation and isolated trading tips.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Our platform brings together education, research and systematic market frameworks to help learners develop
            a deeper understanding of how markets work.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-7 text-sm font-semibold text-navy hover:text-gold transition-colors" data-testid="about-approach-link">
            Our Approach <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-4">
          {THREE_PILLAR_MODEL.map((c) => {
            const Icon = iconMap[c.icon];
            return (
              <div key={c.title} className="flex gap-5 p-6 bg-white border border-border rounded-lg hover:shadow-md transition-shadow">
                <span className="shrink-0 h-12 w-12 rounded-md bg-navy inline-flex items-center justify-center">
                  <Icon className="h-5 w-5 text-gold" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">{c.title}</h3>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LearningPath() {
  return (
    <section className="bg-subtle border-y border-border" data-testid="learning-path-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl">
          <SectionLabel>Learning Architecture</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">The KRV Prime Learning Path</h2>
          <p className="mt-4 text-slate-600">Progressive stages from foundation to advanced frameworks — with clear structure at every level.</p>
        </div>
        <div className="space-y-6 mt-12">
          {LEARNING_PATH.map((lvl, i) => (
            <div key={lvl.level} className="grid md:grid-cols-4 gap-6 border border-border rounded-lg p-7 bg-white" data-testid={`learning-level-${i + 1}`}>
              <div className="md:col-span-1">
                <span className="eyebrow text-gold">{lvl.level}</span>
                <h3 className="font-display text-2xl font-bold text-navy mt-1">{lvl.name}</h3>
                <div className="h-1 w-24 bg-slate-100 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-navy rounded-full" style={{ width: `${(i + 1) * 25}%` }} />
                </div>
              </div>
              <div className="md:col-span-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                {lvl.topics.map((t) => (
                  <span key={t} className="inline-flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedCourses({ courses }) {
  return (
    <section className="bg-subtle border-y border-border" data-testid="featured-courses-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <SectionLabel>Courses</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">Featured Courses</h2>
          </div>
          <Link to="/courses" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors" data-testid="view-all-courses">
            View All Courses <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => <CourseCard key={c.slug} course={c} />)}
        </div>
      </div>
    </section>
  );
}

function ResearchDeskSection({ reports }) {
  return (
    <section className="bg-navy grain" data-testid="research-desk-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <SectionLabel light>Market Insights</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">Systematic Market Insights & Analysis</h2>
            <div className="mt-8 space-y-4">
              {RESEARCH_DESK.map((d) => (
                <div key={d.title} className="border-b border-white/10 pb-4">
                  <h3 className="font-display text-white text-base font-semibold">{d.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{d.items.join(" · ")}</p>
                </div>
              ))}
            </div>
            <Link to="/research" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-gold hover:text-gold-soft transition-colors" data-testid="explore-research-desk">
              Explore the Market Insights <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-semibold text-white">Latest Market Insights</h3>
              <Link to="/research" className="text-sm font-medium text-gold hover:text-gold-soft transition-colors" data-testid="view-all-research">View all</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {reports.map((r) => <InsightCard key={r.slug} report={r} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MaeeSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="maee-section">
      <div className="text-center max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="h-px w-8 bg-gold" /><span className="eyebrow text-gold">Flagship Framework</span><span className="h-px w-8 bg-gold" />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">The MAEE Framework</h2>
        <p className="mt-4 text-slate-600">An educational framework developed for systematic market analysis — Market Structure to Exit Trigger.</p>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mt-14 relative">
        {MAEE.map((m, i) => (
          <div key={i} className="relative bg-white border border-border rounded-lg p-7 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`maee-stage-${i}`}>
            <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-navy font-display text-2xl font-bold text-gold mx-auto">{m.letter}</span>
            <h3 className="font-display text-lg font-semibold text-navy mt-5">{m.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{m.desc}</p>
            {i < MAEE.length - 1 && (
              <ArrowRight className="hidden md:block absolute top-1/2 -right-6 -translate-y-1/2 h-5 w-5 text-gold z-10" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function StrategyLibrary() {
  const [strategies, setStrategies] = useState([]);
  useEffect(() => {
    api.get("/strategies").then((r) => setStrategies(r.data)).catch(() => {});
  }, []);
  return (
    <section className="bg-subtle border-y border-border" data-testid="strategy-library-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <SectionLabel>Strategy & Framework Library</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">Educational Trading Systems Library</h2>
            <p className="mt-4 text-slate-600">Each framework studied for concept, market conditions, components and risk — not as guaranteed-profit systems.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-lg overflow-hidden">
          {strategies.map((s) => (
            <Link key={s.slug} to={`/strategy/${s.slug}`} data-testid={`strategy-${s.slug}`} className="group bg-white p-6 hover:bg-navy transition-colors duration-300">
              <div className="flex items-center justify-between">
                <span className="eyebrow text-gold">{s.condition}</span>
                <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-gold transition-colors" />
              </div>
              <h3 className="font-display text-lg font-semibold text-navy group-hover:text-white mt-3 transition-colors">{s.name}</h3>
              <p className="mt-2 text-sm text-slate-500 group-hover:text-slate-300 line-clamp-2 transition-colors">{s.concept}</p>
            </Link>
          ))}
        </div>
        <Link to="/strategy" className="inline-flex items-center gap-2 mt-10 text-sm font-semibold text-navy hover:text-gold transition-colors" data-testid="view-all-strategies">
          View the Full Strategy Library <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function AlgoFramework() {
  const steps = [
    { icon: Compass, title: "Hypothesis & Edge", desc: "Define a testable market idea — the specific inefficiency or pattern the system aims to exploit." },
    { icon: Boxes, title: "Rules & Logic", desc: "Translate the idea into precise, unambiguous entry, exit and filter rules a machine can follow." },
    { icon: LineChart, title: "Backtesting", desc: "Validate the rules on historical data — measure edge, drawdown and robustness across regimes." },
    { icon: Shield, title: "Risk & Position Sizing", desc: "Size every position by risk, cap exposure and define the maximum loss before automating." },
    { icon: Layers, title: "Execution & Automation", desc: "Deploy the logic with disciplined, emotion-free execution — manual, semi- or fully automated." },
    { icon: Activity, title: "Monitoring & Review", desc: "Track live vs backtest performance, detect edge decay and iterate with a structured journal." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="algo-framework-section">
      <div className="max-w-2xl">
        <SectionLabel>Systematic & Algo Trading</SectionLabel>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">The Systematic Trading Framework</h2>
        <p className="mt-4 text-slate-600">A structured, repeatable process for building rule-based and algorithmic trading systems — from idea to live monitoring. Studied as a discipline, not a profit guarantee.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {steps.map((s, i) => (
          <div key={s.title} className="relative bg-white border border-border rounded-lg p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`algo-step-${i}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex h-11 w-11 rounded-md bg-navy items-center justify-center"><s.icon className="h-5 w-5 text-gold" /></span>
              <span className="font-display text-3xl font-bold text-slate-100">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <h3 className="font-display text-lg font-semibold text-navy mt-5">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-10">
        <Link to="/courses" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors" data-testid="algo-courses-cta">
          Explore Algo Courses <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/participant-oi" className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-navy/20 text-navy font-semibold text-sm hover:bg-navy/5 transition-colors" data-testid="algo-tools-cta">
          See Data-Driven Tools
        </Link>
      </div>
      <div className="mt-8 rounded-lg bg-subtle border border-border p-5 text-sm text-slate-500">
        <span className="font-semibold text-navy">Educational note:</span> Systematic and algorithmic trading involve significant risk. These frameworks are taught for understanding process and discipline — not as guaranteed-profit systems or investment advice.
      </div>
    </section>
  );
}

function Books() {
  const books = [
    {
      cover: "/books/mastering-pivot-trading.jpg",
      title: "Mastering Pivot Trading",
      subtitle: "The Complete Institutional Trading Framework",
      tagline: "CPR • Traditional Pivot Points • Camarilla Pivot Points • 63 Professional Trading Scenarios • 7 CPR Structural Types",
    },
    {
      cover: "/books/systematic-fno-framework.jpg",
      title: "Systematic F&O Trading Framework",
      subtitle: "A Complete Guide to Index Futures & Options Trading",
      tagline: "MAEE Framework • 15 Strategies • Risk Management & Psychology",
    },
  ];
  return (
    <section id="books" className="bg-navy grain" data-testid="books-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-8 bg-gold" /><span className="eyebrow text-gold">Published Works</span></div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">Books by KRV Prime</h2>
          <p className="mt-4 text-slate-400 leading-relaxed">Two comprehensive frameworks distilled into systematic, in-depth reading — built for disciplined, process-driven market learning.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 mt-14">
          {books.map((b, i) => (
            <div key={b.title} className="group flex flex-col sm:flex-row gap-6 border border-white/10 rounded-xl p-6 hover:bg-white/5 transition-colors" data-testid={`book-card-${i}`}>
              <div className="shrink-0 mx-auto sm:mx-0">
                <img src={b.cover} alt={b.title} className="w-40 h-auto rounded-md shadow-2xl ring-1 ring-white/10 transition-transform duration-500 group-hover:scale-[1.04] group-hover:-rotate-1" data-testid={`book-cover-${i}`} />
              </div>
              <div className="flex flex-col">
                <h3 className="font-display text-xl font-semibold text-white leading-snug">{b.title}</h3>
                <p className="mt-1.5 text-sm font-medium text-gold">{b.subtitle}</p>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{b.tagline}</p>
                <div className="mt-5">
                  <Link to="/books" className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-gold/30 bg-gold/10 text-gold text-sm font-semibold hover:bg-gold/20 transition-colors" data-testid={`book-cta-${i}`}>
                    Read more <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/books" className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:gap-3 transition-all" data-testid="view-all-books">
            Explore both books <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function KnowledgeAndTools({ articles }) {
  const tools = [
    { id: "cpr-matrix", label: "CPR Matrix" },
    { id: "oi-sr", label: "OI Support / Resistance" },
    { id: "cpr-rel", label: "CPR Relationship" },
    { id: "pcr", label: "PCR Reading" },
    { id: "position", label: "Position Size" },
    { id: "rr", label: "Risk / Reward" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-3 gap-14" data-testid="knowledge-tools-section">
      <div className="lg:col-span-2">
        <SectionLabel>Knowledge Center</SectionLabel>
        <h2 className="font-display text-3xl font-bold text-navy leading-tight mb-6">From the Knowledge Center</h2>
        <div>{articles.map((a) => <ArticleCard key={a.slug} article={a} />)}</div>
        <Link to="/knowledge" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-navy hover:text-gold transition-colors" data-testid="view-knowledge">
          Visit Knowledge Center <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div>
        <SectionLabel>Tools</SectionLabel>
        <h2 className="font-display text-3xl font-bold text-navy leading-tight mb-6">Market Tools</h2>
        <div className="bg-white border border-border rounded-lg divide-y divide-border">
          {tools.map((t) => (
            <Link key={t.id} to={`/tools?tab=${t.id}`} className="flex items-center justify-between p-4 hover:bg-subtle transition-colors group" data-testid={`tool-teaser-${t.id}`}>
              <span className="inline-flex items-center gap-3 text-sm font-medium text-navy"><Calculator className="h-4 w-4 text-gold" />{t.label}</span>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-gold transition-colors" />
            </Link>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400">Educational calculation tools — not personalised investment advice.</p>
      </div>
    </section>
  );
}

function Events() {
  return (
    <section className="bg-subtle border-y border-border" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <SectionLabel>Events</SectionLabel>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight mb-12">Upcoming Webinars & Workshops</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {EVENTS.map((e) => (
            <div key={e.title} className="bg-white border border-border rounded-lg p-6 flex flex-col" data-testid="event-card">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="h-3.5 w-3.5 text-gold" />
                {new Date(e.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · {e.time}
              </div>
              <h3 className="font-display text-lg font-semibold text-navy mt-3">{e.title}</h3>
              <p className="mt-2 text-sm text-slate-500 flex-1">{e.desc}</p>
              <p className="mt-3 text-xs text-slate-500">Speaker: {e.speaker}</p>
              <span className={`mt-4 self-start text-xs font-semibold px-3 py-1 rounded-md ${e.status === "Registration Open" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-navy text-gold"}`}>{e.status}</span>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/events" className="inline-flex items-center gap-2 text-navy text-sm font-semibold hover:text-gold hover:gap-3 transition-all" data-testid="view-all-events">
            View all webinars &amp; events <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Brokers() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="institutional-section">
      <div className="bg-navy grain rounded-2xl overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="p-10 lg:p-14">
            <SectionLabel light>Institutional Learning</SectionLabel>
            <h2 className="font-display text-3xl font-bold text-white leading-tight">Partner With KRV Prime</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Financial literacy workshops, market education programs and research methodology sessions for colleges,
              universities, training organizations and corporate learning programs.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-7 h-11 px-6 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors" data-testid="partner-cta">
              Partner With KRV Prime <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="hidden lg:block relative">
            <img src="https://images.unsplash.com/photo-1487958449943-2429e8be8625" alt="Institutional learning" className="h-full w-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  return (
    <figure className="relative w-[340px] shrink-0 bg-white border border-border rounded-lg p-8 flex flex-col mx-3 transition-transform duration-300 ease-out hover:scale-[1.05] hover:shadow-2xl hover:z-10" data-testid="testimonial-card">
      <span className="font-display text-6xl leading-none text-gold/30">&ldquo;</span>
      <blockquote className="-mt-4 text-slate-600 leading-relaxed flex-1">{t.quote}</blockquote>
      <figcaption className="mt-6 pt-5 border-t border-border">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-navy text-gold font-display font-bold inline-flex items-center justify-center">{t.name.charAt(0)}</span>
          <div>
            <p className="font-semibold text-navy text-sm">{t.name}</p>
            {t.role && <p className="text-xs text-slate-500">{t.role}</p>}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

function TestimonialForm({ onDone }) {
  const [form, setForm] = useState({ name: "", role: "", quote: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/testimonials", form);
      toast.success(r.data.message || "Submitted for review. Thank you!");
      setForm({ name: "", role: "", quote: "" });
      onDone && onDone();
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="testimonial-form">
      <div>
        <Label htmlFor="t-name">Your name</Label>
        <Input id="t-name" required value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Rahul Mehta" data-testid="testimonial-name" />
      </div>
      <div>
        <Label htmlFor="t-role">Role / City (optional)</Label>
        <Input id="t-role" value={form.role} onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))} placeholder="e.g. Options Trader, Pune" data-testid="testimonial-role" />
      </div>
      <div>
        <Label htmlFor="t-quote">Your experience</Label>
        <Textarea id="t-quote" required rows={4} value={form.quote} onChange={(e) => setForm((s) => ({ ...s, quote: e.target.value }))} placeholder="Share how KRV Prime helped your learning..." data-testid="testimonial-quote" />
      </div>
      <button type="submit" disabled={busy} className="w-full h-11 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors disabled:opacity-60" data-testid="testimonial-submit">
        {busy ? "Submitting..." : "Submit testimonial"}
      </button>
      <p className="text-xs text-slate-400 text-center">Submissions are reviewed before appearing on the site.</p>
    </form>
  );
}

const DEFAULT_TESTIMONIALS = [
  { quote: "The MAEE framework finally gave my analysis a repeatable structure. I stopped chasing tips and started thinking in process.", name: "Rahul Mehta", role: "Working Professional, Bengaluru" },
  { quote: "The daily participant and CPR analysis is exactly the kind of disciplined, no-hype research I was looking for. Genuinely educational.", name: "Ananya Sharma", role: "Options Trader, Pune" },
  { quote: "As a beginner, the structured learning path made derivatives approachable without the usual noise. Clear, honest and well-paced.", name: "Vikram Iyer", role: "Finance Student, Chennai" },
];

function Testimonials() {
  const [items, setItems] = useState(DEFAULT_TESTIMONIALS);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/testimonials").then((r) => { if (r.data && r.data.length) setItems(r.data); }).catch(() => {});
  }, []);

  const loop = items.length >= 3 ? [...items, ...items] : [...items, ...items, ...items, ...items];

  return (
    <section className="bg-subtle border-y border-border overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4"><span className="h-px w-8 bg-gold" /><span className="eyebrow text-gold">What Learners Say</span><span className="h-px w-8 bg-gold" /></div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy leading-tight">Trusted by a Growing Community of Structured Learners</h2>
        </div>
      </div>

      <div className="group relative pb-14" data-testid="testimonials-marquee">
        <div className="flex w-max animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((t, i) => <TestimonialCard key={i} t={t} />)}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-subtle to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-subtle to-transparent" />
      </div>

      <div className="text-center pb-20">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-navy text-navy font-semibold text-sm hover:bg-navy hover:text-white transition-colors" data-testid="share-testimonial-btn">
              <MessageSquarePlus className="h-4 w-4" /> Share your experience
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="testimonial-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-navy">Share your experience</DialogTitle>
              <DialogDescription>Tell us how KRV Prime has supported your learning. Approved testimonials appear on this page.</DialogDescription>
            </DialogHeader>
            <TestimonialForm onDone={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [reports, setReports] = useState([]);
  const [articles, setArticles] = useState([]);
  const { hash } = useLocation();

  useEffect(() => {
    api.get("/courses?featured=true").then((r) => setCourses(r.data.slice(0, 3)));
    api.get("/research").then((r) => setReports(r.data.slice(0, 2)));
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 4)));
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [hash]);

  return (
    <div data-testid="home-page">
      <Hero />
      <Pillars />
      <About />
      {courses.length > 0 && <FeaturedCourses courses={courses} />}
      {reports.length > 0 && <ResearchDeskSection reports={reports} />}
      <LearningPath />
      <MaeeSection />
      <Books />
      <StrategyLibrary />
      <AlgoFramework />
      <Newsletter />
      {articles.length > 0 && <KnowledgeAndTools articles={articles} />}
      <Events />
      <Brokers />
      <Testimonials />
    </div>
  );
}
